<template>
  <div class="section">
    <div class="container">
      <h1 class="page-title mb-8">ชำระเงิน</h1>

      <div class="checkout-layout">
        <!-- Shipping Form -->
        <div class="checkout-form">
          <div class="card">
            <div class="card-body">
              <h2 class="section-heading">ข้อมูลการจัดส่ง</h2>
              
              <!-- แสดงข้อความถ้า login อยู่ -->
              <div v-if="authStore.isAuthenticated && user" class="member-info-notice">
                <svg class="notice-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span>ข้อมูลถูกกรอกจากบัญชีของคุณแล้ว</span>
                <router-link to="/member/profile" class="edit-profile-link">แก้ไขข้อมูล</router-link>
              </div>
              
              <!-- แสดงข้อความถ้ายังไม่ login -->
              <div v-else class="login-suggestion">
                <span>มีบัญชีอยู่แล้ว?</span>
                <router-link to="/login" class="login-link">เข้าสู่ระบบ</router-link>
                <span class="text-muted">เพื่อกรอกข้อมูลอัตโนมัติ</span>
              </div>
              
              <Form @submit="submitOrder" :validation-schema="checkoutSchema" :initial-values="initialValues" v-slot="{ errors, isSubmitting }">
                <div class="form-group">
                  <label class="form-label">ชื่อ-นามสกุล</label>
                  <Field 
                    name="customer_name"
                    type="text" 
                    class="form-input"
                    :class="{ 'border-red-500': errors.customer_name }"
                    placeholder="สมชาย ใจดี"
                  />
                  <ErrorMessage name="customer_name" class="text-red-500 text-sm mt-1" />
                </div>
                
                <div class="form-group">
                  <label class="form-label">เบอร์โทรศัพท์</label>
                  <Field 
                    name="phone"
                    type="tel" 
                    class="form-input"
                    :class="{ 'border-red-500': errors.phone }"
                    placeholder="0812345678"
                  />
                  <ErrorMessage name="phone" class="text-red-500 text-sm mt-1" />
                  <p class="form-help">กรอกเบอร์โทรศัพท์ 10 หลัก</p>
                </div>

                <div class="form-group">
                  <label class="form-label">ที่อยู่</label>
                  <Field 
                    name="address"
                    as="textarea"
                    class="form-input"
                    :class="{ 'border-red-500': errors.address }"
                    rows="3"
                    placeholder="123 ถนนสุขุมวิท, กรุงเทพฯ 10110"
                  />
                  <ErrorMessage name="address" class="text-red-500 text-sm mt-1" />
                </div>

                <div class="form-group">
                  <label class="form-label">หมายเหตุ (ถ้ามี)</label>
                  <Field 
                    name="notes"
                    as="textarea"
                    class="form-input"
                    rows="2"
                    placeholder="รายละเอียดเพิ่มเติม..."
                  />
                </div>
                
                <div class="payment-method">
                  <h3 class="method-title">วิธีการชำระเงิน</h3>
                  <div class="method-option">
                    <input type="radio" checked disabled>
                    <div>
                      <span class="method-name">โอนเงินผ่านธนาคาร</span>
                      <p class="method-desc">กรุณาโอนเงินยอดรวมไปยังบัญชีธนาคารที่แสดงหลังจากสร้างคำสั่งซื้อ</p>
                    </div>
                  </div>
                </div>

                <button 
                  type="submit" 
                  class="btn btn-primary btn-lg w-full"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="loading-spinner" style="width: 16px; height: 16px; margin-right: 8px;"></span>
                  {{ isSubmitting ? 'กำลังสร้างคำสั่งซื้อ...' : 'สั่งซื้อสินค้า' }}
                </button>
              </Form>
            </div>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="checkout-summary">
          <div class="card summary-card">
            <div class="card-body">
              <h2 class="section-heading">สรุปคำสั่งซื้อ</h2>
              
              <div class="order-items">
                <div v-for="item in cartStore.items" :key="item.id" class="order-item">
                  <span class="item-name">{{ item.quantity }}x {{ item.product_name }}</span>
                  <span class="item-price">{{ formatPrice(item.total_price) }}</span>
                </div>
              </div>
              
              <div class="order-totals">
                <div class="total-row">
                  <span>ยอดรวมสินค้า</span>
                  <span>{{ formatPrice(cartStore.subtotal) }}</span>
                </div>
                <div class="total-row">
                  <span>ค่าจัดส่ง</span>
                  <span>{{ formatPrice(cartStore.shippingFee) }}</span>
                </div>
                <div class="total-row grand-total">
                  <span>ยอดสุทธิ</span>
                  <span>{{ formatPrice(cartStore.totalAmount) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useCartStore } from '../stores/cart';
import { useAuthStore } from '../stores/auth';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import api from '../api';
import { useNotification } from '../composables/useNotification';

const router = useRouter();
const cartStore = useCartStore();
const authStore = useAuthStore();
const { showSuccess, showError, handleApiError } = useNotification();

const user = computed(() => authStore.user);
const formRef = ref(null);

const checkoutSchema = yup.object({
  customer_name: yup.string().required('กรุณากรอกชื่อ-นามสกุล'),
  phone: yup.string()
    .required('กรุณากรอกเบอร์โทรศัพท์')
    .matches(/^[0-9]{10}$/, 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก'),
  address: yup.string().required('กรุณากรอกที่อยู่').min(10, 'ที่อยู่ต้องมีอย่างน้อย 10 ตัวอักษร'),
  notes: yup.string().nullable()
});

// สร้าง initial values จากข้อมูล member
const getInitialValues = () => {
  if (user.value) {
    // รวมที่อยู่จาก field ต่างๆ
    const addressParts = [
      user.value.address,
      user.value.district,
      user.value.province,
      user.value.postal_code
    ].filter(Boolean);
    
    return {
      customer_name: user.value.name || '',
      phone: user.value.phone || '',
      address: addressParts.join(' ') || '',
      notes: ''
    };
  }
  return {
    customer_name: '',
    phone: '',
    address: '',
    notes: ''
  };
};

const initialValues = ref(getInitialValues());

onMounted(async () => {
  cartStore.fetchCart();
  
  // ถ้า login อยู่ ให้ดึงข้อมูล user มา prefill
  if (authStore.isAuthenticated) {
    await authStore.fetchUser();
    initialValues.value = getInitialValues();
  }
});

const formatPrice = (price) => {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(price);
};

const submitOrder = async (values, { setErrors }) => {
  if (cartStore.items.length === 0) {
    showError('ตะกร้าสินค้าของคุณว่างเปล่า');
    return;
  }

  try {
    const response = await api.post('/orders', values);
    await cartStore.fetchCart();
    showSuccess('สั่งซื้อสินค้าสำเร็จ!');
    router.push(`/order/${response.data.data.order_number}`);
  } catch (error) {
    handleApiError(error, setErrors);
  }
};
</script>

<style scoped>
.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
}

.mb-8 {
  margin-bottom: 2rem;
}

.checkout-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

@media (min-width: 1024px) {
  .checkout-layout {
    grid-template-columns: 1fr 400px;
  }
}

.section-heading {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1.5rem;
}

.form-help {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-top: 0.25rem;
}

.payment-method {
  margin: 1.5rem 0;
  padding: 1rem;
  background: var(--color-bg);
  border-radius: 0.5rem;
}

.method-title {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.method-option {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.method-name {
  font-weight: 500;
}

.method-desc {
  font-size: 0.875rem;
  color: var(--color-text-light);
  margin-top: 0.25rem;
}

.summary-card {
  position: sticky;
  top: 80px;
}

.order-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.order-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
}

.item-name {
  color: var(--color-text-light);
}

.item-price {
  color: var(--color-text);
}

.order-totals {
  border-top: 1px solid var(--color-border);
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.total-row {
  display: flex;
  justify-content: space-between;
  color: var(--color-text-light);
}

.total-row.grand-total {
  border-top: 1px solid var(--color-border);
  padding-top: 0.75rem;
  margin-top: 0.5rem;
  font-weight: 700;
  font-size: 1.125rem;
  color: var(--color-primary);
}

.border-red-500 {
  border-color: #ef4444 !important;
}

.text-red-500 {
  color: #ef4444;
}

.text-sm {
  font-size: 0.875rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.member-info-notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: #065f46;
}

.notice-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #10b981;
  flex-shrink: 0;
}

.edit-profile-link {
  margin-left: auto;
  color: var(--color-primary);
  font-weight: 500;
  text-decoration: none;
}

.edit-profile-link:hover {
  text-decoration: underline;
}

.login-suggestion {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: #92400e;
}

.login-link {
  color: var(--color-primary);
  font-weight: 600;
  text-decoration: none;
}

.login-link:hover {
  text-decoration: underline;
}

.text-muted {
  color: #a16207;
}
</style>
